import React from 'react';
import {
  Box,
  Chip,
  Divider,
  Drawer,
  IconButton,
  LinearProgress,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import type { DroppedAudienceData, FunnelInsightNodeMetric } from '../types';
import { ANALYTICS_CANVAS_TEST_IDS } from '../testIds';
import { formatPct } from '../statTierCopy';
import { AudienceProvenanceBucketList, AudienceProvenanceSettingsButton } from './AudienceProvenanceBuckets';
import { useAudienceProvenanceSettings } from '../audienceProvenance';

/**
 * I-A — «Left after {screen}» (P-1/P-2, approved mock): per-variable
 * distributions of the LEAVERS with an over/under-index delta against
 * everyone who reached the screen (grey baseline tick on each bar, ±pts
 * pill), top leaver combinations + «other», explicit-exit caption framed as a
 * subset of ended-here. Loading skeleton and an honest degraded note; D-6
 * absence copy on 0 leavers / empty profile (never fake zero bars).
 */

/** Same honesty threshold as the Screen Audience Profile (D-5). */
const AUDIENCE_DIRECTION_ONLY_MAX_N = 30;
/** D-3 idiom: UI shows the top combinations; the rest folds into «other». */
const DROPOFF_COMBOS_SHOWN = 10;
/** Deltas smaller than this render no pill — direction noise, not signal. */
const DELTA_MIN_PTS = 0.005;

interface DropoffAudienceDrawerProps {
  open: boolean;
  onClose: () => void;
  screenName?: string;
  screenType?: string;
  /** The stub's node metric — headline −N and the explicit-exit caption. */
  metric?: FunnelInsightNodeMetric | null;
  audience: DroppedAudienceData | null;
  loading?: boolean;
  /** Non-null = the fetch failed — degraded note, node numbers unaffected. */
  error?: string | null;
  /** Active canvas filters do NOT scope this v1 payload — say it. */
  filtersActive?: boolean;
}

export const DropoffAudienceDrawer: React.FC<DropoffAudienceDrawerProps> = ({
  open,
  onClose,
  screenName,
  screenType,
  metric,
  audience,
  loading = false,
  error = null,
  filtersActive = false,
}) => {
  const { settings, setBucketShown } = useAudienceProvenanceSettings();
  const dropped = audience?.droppedPeople ?? metric?.flowLossCount ?? 0;
  const reached = audience?.reachedPeople ?? 0;
  const explicitExits = metric?.exits ?? 0;
  const silent = Math.max(0, dropped - explicitExits);
  const shownCombos = (audience?.combos ?? []).slice(0, DROPOFF_COMBOS_SHOWN);
  const shownComboUsers = shownCombos.reduce((sum, combo) => sum + combo.users, 0);
  const otherUsers = audience ? Math.max(0, audience.droppedPeople - shownComboUsers) : 0;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: 360, p: 2 } }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Typography variant="h6">{`Left after «${screenName ?? 'screen'}»`}</Typography>
        <IconButton
          aria-label="close drop-off details"
          data-testid={ANALYTICS_CANVAS_TEST_IDS.dropoffDrawerClose}
          onClick={onClose}
          size="small"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        {[screenType, 'read-only', filtersActive ? 'unfiltered — canvas filters do not apply here yet' : undefined]
          .filter(Boolean)
          .join(' · ')}
      </Typography>

      <Stack spacing={1.5} data-testid={ANALYTICS_CANVAS_TEST_IDS.dropoffDrawer}>
        <Box>
          <Typography variant="subtitle1" sx={{ color: 'error.main', fontWeight: 700 }}>
            {`−${dropped.toLocaleString('en-US')} people`}
            {reached > 0 && ` · ${formatPct(dropped / reached)} of reachers`}
          </Typography>
          {/* RC-AC-051: explicit exits framed as a SUBSET of ended-here. */}
          <Typography variant="caption" color="text.secondary">
            {`${explicitExits.toLocaleString('en-US')} explicit exits · ${silent.toLocaleString('en-US')} left without an exit event`}
          </Typography>
          {/* PA-AC-007: the returnee split answers «why does this look weird»
              before it is asked; absent at 0 (D-6). */}
          {Boolean(audience?.returnees) && (
            <Typography
              variant="caption"
              sx={{ display: 'block' }}
              data-testid={ANALYTICS_CANVAS_TEST_IDS.audienceReturnees}
            >
              <Box component="span" sx={{ fontWeight: 700 }}>
                {`${audience!.returnees!.toLocaleString('en-US')} of them`}
              </Box>
              {` had been in the funnel before${dropped > 0 ? ` (${formatPct(audience!.returnees! / dropped)})` : ''}`}
            </Typography>
          )}
        </Box>

        {loading && (
          <Stack spacing={1}>
            <Skeleton variant="text" width={260} />
            <Skeleton variant="rounded" height={18} />
            <Skeleton variant="rounded" height={18} />
            <Skeleton variant="rounded" height={18} />
          </Stack>
        )}

        {!loading && error && (
          <Typography
            variant="caption"
            color="text.secondary"
            data-testid={ANALYTICS_CANVAS_TEST_IDS.dropoffDrawerDegraded}
          >
            Drop-off audience is unavailable right now — the canvas numbers above are unaffected. Try again later.
          </Typography>
        )}

        {!loading && !error && !audience && (
          <Typography
            variant="caption"
            color="text.secondary"
            data-testid={ANALYTICS_CANVAS_TEST_IDS.dropoffDrawerEmpty}
          >
            No audience profile for the people who left here in this window — nothing to break down.
          </Typography>
        )}

        {!loading && !error && audience && (
          <>
            <Divider />
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Typography variant="overline" color="text.secondary">
                Who left here · answered on the way here
              </Typography>
              <Tooltip
                title="Red bars = share among the people who LEFT at this step; the grey tick = the same value's share among everyone who reached. Values are each person's latest answer given in the SAME visit, before or on this screen — answers from screens ahead and from earlier visits are counted separately below; expand them for the back-navigation or returnee story."
              >
                <InfoOutlinedIcon
                  data-testid={ANALYTICS_CANVAS_TEST_IDS.dropoffDrawerTooltip}
                  sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }}
                />
              </Tooltip>
              {Boolean(audience.buckets?.after || audience.buckets?.other) && (
                <AudienceProvenanceSettingsButton settings={settings} onToggle={setBucketShown} />
              )}
            </Stack>

            {audience.variables.map(variable => (
              <Stack
                key={variable.variableKey}
                spacing={0.75}
                data-testid={ANALYTICS_CANVAS_TEST_IDS.dropoffDrawerVariable}
              >
                <Typography variant="overline" color="text.secondary">{variable.variableKey}</Typography>
                {variable.values.map(entry => (
                  <DeltaShareRow
                    key={entry.value}
                    label={entry.value}
                    users={entry.users}
                    share={entry.share}
                    baselineShare={entry.baselineShare}
                    directionOnly={entry.users < AUDIENCE_DIRECTION_ONLY_MAX_N}
                  />
                ))}
                {variable.noValueUsers > 0 && (
                  <DeltaShareRow
                    label="no value recorded"
                    users={variable.noValueUsers}
                    share={audience.droppedPeople > 0 ? variable.noValueUsers / audience.droppedPeople : 0}
                    residual
                  />
                )}
              </Stack>
            ))}

            <AudienceProvenanceBucketList
              buckets={audience.buckets}
              settings={settings}
              onToggle={setBucketShown}
              afterCaption="went further, came back and left here — screens ahead may be pushing them back"
              otherCaption="answers given on earlier passes through the funnel — profile of people who came back and left here"
              barColor="error"
            />

            {shownCombos.length > 0 && (
              <Stack spacing={0.5} data-testid={ANALYTICS_CANVAS_TEST_IDS.dropoffDrawerCombos}>
                <Typography variant="overline" color="text.secondary">
                  Top combinations among leavers
                </Typography>
                {shownCombos.map((combo, index) => (
                  <ComboLine
                    key={combo.pairs.map(pair => `${pair.variableKey}=${pair.value}`).join('|')}
                    index={index}
                    label={combo.pairs.map(pair => `${pair.variableKey}: ${pair.value}`).join(' · ')}
                    users={combo.users}
                    share={combo.share}
                    directionOnly={combo.users < AUDIENCE_DIRECTION_ONLY_MAX_N}
                  />
                ))}
                {otherUsers > 0 && (
                  <ComboLine
                    index={shownCombos.length}
                    label="other"
                    users={otherUsers}
                    share={audience.droppedPeople > 0 ? otherUsers / audience.droppedPeople : 0}
                    residual
                  />
                )}
              </Stack>
            )}
          </>
        )}
      </Stack>
    </Drawer>
  );
};

/**
 * P-2: dropped-share bar + grey baseline tick at the reached share + ±pts
 * delta pill (red over-index, green under-index). No baseline → no tick, no
 * pill — absence over invented zeros.
 */
function DeltaShareRow({
  label,
  users,
  share,
  baselineShare,
  directionOnly = false,
  residual = false,
}: {
  label: string;
  users: number;
  share: number;
  baselineShare?: number;
  directionOnly?: boolean;
  residual?: boolean;
}): React.ReactElement {
  const delta = baselineShare === undefined ? undefined : share - baselineShare;
  const showDelta = delta !== undefined && Math.abs(delta) >= DELTA_MIN_PTS && !directionOnly;
  return (
    <Box>
      <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="baseline">
        <Typography
          variant="body2"
          sx={{
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            ...(residual && { color: 'text.secondary', fontStyle: 'italic' }),
          }}
        >
          {label}
          {showDelta && (
            <Chip
              size="small"
              label={`${delta! > 0 ? '+' : '−'}${Math.abs(delta! * 100).toFixed(0)} pts vs reached`}
              sx={{
                ml: 0.5,
                height: 16,
                fontSize: 10,
                fontWeight: 600,
                color: delta! > 0 ? 'error.main' : 'success.main',
                bgcolor: theme => delta! > 0
                  ? `${theme.palette.error.main}1f`
                  : `${theme.palette.success.main}1f`,
              }}
            />
          )}
          {directionOnly && (
            <Typography component="span" variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', ml: 0.5 }}>
              ~ direction only
            </Typography>
          )}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          {`${users.toLocaleString('en-US')} · ${formatPct(share)}`}
        </Typography>
      </Stack>
      <Box sx={{ position: 'relative' }}>
        <LinearProgress
          variant="determinate"
          value={Math.max(0, Math.min(100, share * 100))}
          color={residual ? 'inherit' : 'error'}
          sx={{ height: 4, borderRadius: 1, ...(residual && { opacity: 0.45 }) }}
        />
        {baselineShare !== undefined && (
          <Box
            sx={{
              position: 'absolute',
              top: -2,
              bottom: -2,
              width: 2,
              bgcolor: 'text.secondary',
              opacity: 0.9,
              left: `${Math.max(0, Math.min(100, baselineShare * 100))}%`,
            }}
          />
        )}
      </Box>
    </Box>
  );
}

function ComboLine({
  index,
  label,
  users,
  share,
  directionOnly = false,
  residual = false,
}: {
  index: number;
  label: string;
  users: number;
  share: number;
  directionOnly?: boolean;
  residual?: boolean;
}): React.ReactElement {
  return (
    <Stack
      direction="row"
      spacing={1}
      justifyContent="space-between"
      alignItems="baseline"
      data-testid={ANALYTICS_CANVAS_TEST_IDS.dropoffDrawerCombo(index)}
    >
      <Typography
        variant="body2"
        sx={{
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          ...(residual && { color: 'text.secondary', fontStyle: 'italic' }),
        }}
      >
        {label}
        {directionOnly && (
          <Typography component="span" variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', ml: 0.5 }}>
            ~ direction only
          </Typography>
        )}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
        {`${users.toLocaleString('en-US')} · ${formatPct(share)}`}
      </Typography>
    </Stack>
  );
}
